<template>
	<div>
		<template v-if="!onlyList">
			<v-card-title class="d-flex flex-row align-items-center py-0 px-4" style="height: 60px">
				<v-btn-toggle
					:value="tab"
					class="mr-1"
					mandatory>
					<v-tooltip top>
						<template v-slot:activator="{ on }">

							<v-btn
								@click="setTab('private')"
								v-on="on"
								:value="'private'"
								color="white"
								small>
								<v-icon small>mdi-lock-outline</v-icon> {{ $vuetify.lang.t('$vuetify.views.chat.rooms.private') }}
								<v-badge
									v-if="unreadPrivateCount"
									inline
									color="red"
									:content="unreadPrivateCount >= 100 ? '99+' : unreadPrivateCount"
								>
								</v-badge>
							</v-btn>
						</template>
						{{ $vuetify.lang.t('$vuetify.views.chat.rooms.roomsPrivate') }}
					</v-tooltip>
					<v-tooltip top>
						<template v-slot:activator="{ on }">
							<v-btn
								@click="setTab('public')"
								v-on="on"
								:value="'public'"
								color="white"
								small>
								<v-icon small>mdi-web</v-icon> {{ $vuetify.lang.t('$vuetify.views.chat.rooms.public') }}
								<v-badge
									v-if="unreadPublicCount"
									inline
									color="red"
									:content="unreadPublicCount >= 100 ? '99+' : unreadPublicCount"
								>
								</v-badge>
							</v-btn>
						</template>
						{{ $vuetify.lang.t('$vuetify.views.chat.rooms.roomsPublic') }}
					</v-tooltip>
				</v-btn-toggle>
				<div class="ml-auto">
					<v-tooltip bottom :open-delay="500">
						<template v-slot:activator="{ on }">
							<v-btn
								v-on="on"
								@click="openNewMessagePopup"
								icon
								plain
								width="20"
								color="black">
								<v-icon>mdi-plus-circle-outline</v-icon>
							</v-btn>
						</template>
						{{ $vuetify.lang.t('$vuetify.views.chat.messages.newMessage') }}
					</v-tooltip>
					<v-tooltip v-if="expand" bottom :open-delay="500">
						<template v-slot:activator="{ on }">
							<v-btn
								:to="{name: 'Chat'}"
								v-on="on"
								icon
								plain
								width="20"
								color="black"
								class="ml-2"
							>
								<v-icon>$external-link</v-icon>
							</v-btn>
						</template>
						{{ $vuetify.lang.t('$vuetify.views.chat.expand') }}
					</v-tooltip>
					<v-tooltip v-if="minimize" bottom :open-delay="500">
						<template v-slot:activator="{ on }">
							<v-btn
								v-on="on"
								@click="toggleMenu()"
								icon
								plain
								width="20"
								color="black"
								class="ml-2"
							>
								<v-icon>mdi-chevron-down</v-icon>
							</v-btn>
						</template>
						{{ $vuetify.lang.t('$vuetify.views.chat.minimize') }}
					</v-tooltip>
				</div>
			</v-card-title>
			<v-card-title class="pt-0 pb-2 px-4">
				<v-text-field
						v-model="filters.search"
						:placeholder="$vuetify.lang.t('$vuetify.views.chat.messages.searchPlaceholder')"
						outlined
						dense
						:loading="loadings.search"
						@input="search()"
						prepend-inner-icon="mdi-magnify"
						class="font-14 mt-3"
						hide-details
						clearable
					>
				</v-text-field>
			</v-card-title>
		</template>
		<v-card-text :class="bodyClass">
			<div v-if="loadingRooms || loadings.search">
				<template v-for="i in 5">
					<v-skeleton-loader
						:key="i"
						type="list-item-avatar-two-line"
					></v-skeleton-loader>
				</template>
			</div>
			<v-list
				v-else-if="currentRooms.length"
				v-model="selectedRoom"
				class="py-0"
			>
				<template v-for="(room, i) in currentRooms">
					<v-divider v-if="i == 0" :key="`start-divider-${room.id}`" />
					<v-skeleton-loader
						v-if="!room.is_public && !room.is_direct && (room.users && !room.users.length)"
						:key="'skeleton_' + i"
						type="list-item-avatar-two-line"
					></v-skeleton-loader>
					<v-list-item
						v-else
						:key="room.id"
						@click="openRoom(room)"
						:value="room.id"
						flat
						:class="{'room-active': roomActive(room)}"
						:ref="'room__' + room.hash"
						class="py-2"
					>
						<v-badge
							v-if="room.bubble_expanded"
							overlap
							bordered
							top
							offset-y="-10"
							offset-x="5"
							color="primary"
							style="z-index: 1"
							icon="mdi-arrow-left"
						/>
						<v-badge
							v-if="room.is_public"
							overlap
							bordered
							bottom
							offset-y="0"
							offset-x="5"
							style="z-index: 1"
							class="badge-public"
							icon="mdi-web"
						/>
						<v-avatar
							color="accent"
							size="40"
							v-if="room.is_public"
							><v-icon color="white">mdi-account</v-icon></v-avatar>
						<avatar v-else-if="getOtherUsers(room).length == 1" :user="getOtherUsers(room)[0]" />
						<avatar v-else :users="getOtherUsers(room)" multiple />
						<v-badge
							v-if="room.unread_messages_count"
							overlap
							bordered
							top
							offset-y="0"
							offset-x="15"
							color="red"
							:content="room.unread_messages_count >= 100 ? '99+' : room.unread_messages_count"
						>
						</v-badge>

						<v-list-item-content class="ml-2 d-flex w-100">
							<div class="d-flex flex-row w-100">
								<div class="username bold font-14 overflow-dots">
									{{ getRoomName(room) }}
									<div v-if="getOtherUsers(room).length > 1" class="font-12">{{ getOtherUsers(room).length }} {{ $vuetify.lang.t('$vuetify.views.chat.membersCount') }}</div>
								</div>
								<div v-if="room.latest_message" class="ml-auto nowrap pl-2">
									<v-tooltip top>
										<template v-slot:activator="{ on }">
											<span v-on="on">
												{{ $moment(room.latest_message.created_at).fromNow() }}
											</span>
										</template>
										{{ $moment(room.latest_message.created_at).local().format('YYYY-MM-DD HH:MM') }}
									</v-tooltip>
								</div>
							</div>
							<div v-if="room.latest_message">
								{{ room.latest_message.content }}
								<div class="d-flex flex-row align-items-center">
									<div v-if="room.latest_message.images_count" class="mr-1">
										<v-icon small>$photo</v-icon>
									</div>
									<div v-if="room.latest_message.attachments_count" class="mr-1">
										<v-icon small>mdi-attachment</v-icon>
									</div>
								</div>
							</div>
						</v-list-item-content>
					</v-list-item>
					<v-divider :key="`divider-${room.id}`" />
				</template>
			</v-list>
			<v-list v-else>
				<v-list-item class="text-center">
					{{ $vuetify.lang.t('$vuetify.views.chat.rooms.noRooms') }}
				</v-list-item>
			</v-list>
		</v-card-text>
	</div>
</template>

<script>
import {mapGetters} from 'vuex';
import _ from 'lodash';
import Avatar from '@/components/Avatar';
import axios from 'axios';
import {getOtherUsers, getChatName} from '@/functions/chat';
export default {
	components: {
		Avatar
	},
	props: {
		bodyClass: {
			type: String,
			default: 'chat__quickmenu-body'
		},
		expand: {
			type: Boolean,
			default: false
		},
		minimize: {
			type: Boolean,
			default: false
		},
		unread: {
			type: Boolean,
			default: false
		},
		onlyList: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			selectedRoom: null,
			filters: {
				search: ''
			},
			searchedRooms: [],
			loadings: {
				search: false
			},
			showTab: false
		}
	},
	watch: {
		loadingRooms: function() {
			this.scroll();
		}
	},
	computed: {
		...mapGetters({
			user: 'auth/user',
			rooms: 'chat/rooms',
			loadingRooms: 'chat/loadingRooms',
			tab: 'chat/tab'
		}),
		unreadPrivateCount() {
			return _.sumBy(this.rooms.filter(x => !x.is_public), 'unread_messages_count');
		},
		unreadPublicCount() {
			return _.sumBy(this.rooms.filter(x => x.is_public), 'unread_messages_count');
		},
		currentRooms() {
			if(this.filters.search && this.filters.search.length >= 2) {
				return this.searchedRooms;
			}
			if(this.unread) {
				return _.orderBy(this.rooms.filter(x => x.unread_messages_count > 0), 'latest_message.updated_at','desc');
			}
			if(this.tab == 'public') {
				return _.orderBy(this.rooms.filter(x => x.is_public), [x => x.name.toLowerCase()],'asc');
			}
			else if(this.tab == 'private') {
				return _.orderBy(this.rooms.filter(x => !x.is_public), 'latest_message.updated_at','desc');
			}
			return _.orderBy(this.rooms, 'latest_message.updated_at', 'desc');
		},
	},
	methods: {
		scroll() {
			if(this.$route.params.hash) {
				if(this.rooms.length && !this.loadingRooms) {
					let element = this.$refs['room__' + this.$route.params.hash];
					if(element && element[0]) {
						element[0].$el.scrollIntoView();
					}
				}
			}
		},
		getRoomName(room) {
			return getChatName(this.user, room);
		},
		getOtherUsers(room) {
			return getOtherUsers(this.user, room);
		},
		setTab(type) {
			this.$store.commit('chat/setTab', type);
			this.showTab = false;
			if(this.filters.search) {
				this.search();
			}
			if(this.$route.name == 'Chat' && this.$route.params.hash) {
				this.$router.replace({name: 'Chat'})
			}
		},
		roomActive(room) {
			if(this.$route.name == 'Chat' && this.$route.params.hash == room.hash) {
				return true;
			}
			return false;
		},
		openNewMessagePopup() {
			this.$emit('openNewMessagePopup')
		},
		toggleMenu() {
			this.$emit('toggleMenu');
		},
		async search() {
			// Search rooms
			if(this.filters.search && this.filters.search.length >= 2) {
				this.loadings.search = true;
				await axios.get('chat/messages', {
					params: {
						search: this.filters.search
					}
				})
				.then(response => {
					let ids = [];
					response.data.data.forEach(x => {
						if(ids.findIndex(id => id == x.chat_room_id) == -1) {
							ids.push(x.chat_room_id);
						}
					});
					this.searchedRooms = this.rooms.filter(x => {
						if(this.tab == 'public') {
							return ids.indexOf(x.id) != -1 && x.is_public == true;
						}
						return ids.indexOf(x.id) != -1 && x.is_public == false;
					});
				})
				.catch(error => {
					console.log("ERROR SEARCH", error)
				})

				this.loadings.search = false;
			}
			else {
				this.searchedRooms = [];
			}
		},
		openRoom(room) {
			this.filters.search = '';
			this.searchedRooms = [];
			this.$emit('openRoom', room);
		}
	},
	created() {
		this.search = _.debounce(this.search, 300);
		setTimeout(() => {
			this.scroll();
		}, 300)

	}
}
</script>

<style lang="scss">
	.room-active {
		background: var(--v-accent-lighten2);
	}
	.badge-public {
		.v-badge__badge .v-icon {
			font-size: 14px;
		}
	}
</style>
